import type { ReactNode } from 'react';

export interface PanelPresentationProps {
  presentation: 'sidebar' | 'business';
  onClose(): void;
}
export interface PanelDefinition {
  workspaceId: string;
  resolve(params: unknown):
    | {
        render(props: PanelPresentationProps): ReactNode;
        navigationSearch?: string;
        entrySelector?: string;
      }
    | undefined;
}
export interface PanelAction {
  id: string;
  target: string;
  workspaceId: string;
  navigationSearch?: string;
  entrySelector?: string;
  render(props: PanelPresentationProps): ReactNode;
}

/** Each business adapter owns its parameter types; the host only handles presentation. */
export function definePanel<T>(definition: {
  workspaceId: string;
  parseParams(raw: unknown): T | undefined;
  navigationSearch?(params: T): string;
  entrySelector?: string;
  render(params: T, props: PanelPresentationProps): ReactNode;
}): PanelDefinition {
  return {
    workspaceId: definition.workspaceId,
    resolve(raw) {
      const params = definition.parseParams(raw);
      return params === undefined
        ? undefined
        : {
            render: (props) => definition.render(params, props),
            navigationSearch: definition.navigationSearch?.(params),
            ...(definition.entrySelector ? { entrySelector: definition.entrySelector } : {}),
          };
    },
  };
}

export function resolvePanelAction(
  raw: unknown,
  registry: Record<string, PanelDefinition>,
): PanelAction | undefined {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (
      !value ||
      value.version !== 1 ||
      !Array.isArray(value.widgets) ||
      value.widgets.length !== 1
    )
      return;
    const widget = value.widgets[0];
    if (
      !widget ||
      typeof widget !== 'object' ||
      Array.isArray(widget) ||
      Object.keys(widget).some((key) => !['type', 'id', 'panel', 'params'].includes(key)) ||
      widget.type !== 'workspace_panel' ||
      typeof widget.id !== 'string' ||
      !widget.id.trim() ||
      widget.id.length > 64 ||
      typeof widget.panel !== 'string' ||
      !Object.prototype.hasOwnProperty.call(registry, widget.panel)
    )
      return;
    const definition = registry[widget.panel];
    const resolved = definition.resolve(widget.params === undefined ? {} : widget.params);
    return resolved
      ? {
          id: widget.id.trim(),
          target: widget.panel,
          workspaceId: definition.workspaceId,
          ...resolved,
        }
      : undefined;
  } catch {
    return;
  }
}
